"use client";

import { useCallback, useMemo, useState } from "react";
import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import {
  getMessageFromNetworkError,
  useBulkDeleteAssets,
  useBulkRestoreAssets,
  useBulkTrashAssets,
  useFavoriteAssets,
  useTablesConnectedAssets,
} from "@zapier/client";
import { AssetTypes, FOLDER_TRASH_ID, useNotification } from "@zapier/utils";
import { useBulkActionLimitGuard } from "../../../hooks/useBulkActionLimitGuard";
import { TrashNotification } from "../../common/TrashNotification/TrashNotification";
import { UndeletableAssetsList } from "../../common/LinkedAssetsWarning/UndeletableAssetsList";
import styles from "./AssetDeleteModal.module.css";

interface TypedAssetId {
  assetType: string;
  assetId: string;
}

// Asset ids here are typed (e.g. "canvas:abc123"), matching convertToAssetId's
// "<assetType>:<assetId>" format.
function parseTypedAssetId(typedAssetId: string): TypedAssetId | undefined {
  const separatorIndex = typedAssetId.indexOf(":");
  if (separatorIndex === -1) {
    return undefined;
  }
  return {
    assetType: typedAssetId.slice(0, separatorIndex),
    assetId: typedAssetId.slice(separatorIndex + 1),
  };
}

function isTypedAssetId(
  parsed: TypedAssetId | undefined,
): parsed is TypedAssetId {
  return parsed !== undefined;
}

// The modal/toast noun: the shared type, or "other" for a mixed selection.
export function getAssetTypeLabel(
  typedAssetIds: string[],
): "canvas" | "zap" | "table" | "other" {
  const assetTypes = new Set(
    typedAssetIds.map(
      (typedAssetId) => parseTypedAssetId(typedAssetId)?.assetType,
    ),
  );
  if (assetTypes.size !== 1) {
    return "other";
  }
  const [assetType] = assetTypes;
  if (assetType === AssetTypes.Canvas) {
    return "canvas";
  }
  if (assetType === AssetTypes.Zap) {
    return "zap";
  }
  if (assetType === AssetTypes.Table) {
    return "table";
  }
  return "other";
}

interface AssetDeletePermissionResult {
  deletableIds: string[];
  nonDeletableIds: string[];
  isLoading: boolean;
  hasChecked: boolean;
}

interface AssetDeleteModalProps {
  selectedAssetIds: string[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  folderId?: string;
  onSettled?: () => void;
  // When a single asset is selected, its name lets the move-to-trash title read
  // "Move “Name” to trash?" instead of "Move 1 item to trash?".
  singleAssetName?: string;
  // Omit to skip permission filtering (every selected asset is deletable).
  permissionResult?: AssetDeletePermissionResult;
  // Raw-id -> display name, for the "won't be deleted" list.
  assetNamesById?: Record<string, string>;
}

const EMPTY_IDS: string[] = [];

export function AssetDeleteModal({
  selectedAssetIds,
  isOpen,
  setIsOpen,
  folderId,
  onSettled,
  singleAssetName,
  permissionResult,
  assetNamesById,
}: AssetDeleteModalProps) {
  const t = useTranslations("bulk-delete-modal");
  const { createNotification } = useNotification();
  const notifyIfOverBulkLimit = useBulkActionLimitGuard("delete");
  const isTrash = folderId === FOLDER_TRASH_ID;
  const bulkTrashAssets = useBulkTrashAssets({ folderId });
  const bulkDeleteAssets = useBulkDeleteAssets();
  const bulkRestoreAssets = useBulkRestoreAssets({ folderId });
  const { removeManyFromFavorites } = useFavoriteAssets();
  const [isDeleting, setIsDeleting] = useState(false);

  // No permissionResult means permission filtering is skipped: every
  // selected asset is treated as deletable.
  const {
    deletableIds = selectedAssetIds,
    nonDeletableIds = EMPTY_IDS,
    isLoading: isPermLoading = false,
    hasChecked = true,
  } = permissionResult ?? {};

  const itemCount =
    !hasChecked || isPermLoading
      ? selectedAssetIds.length
      : deletableIds.length;
  const idsToDelete = hasChecked ? deletableIds : selectedAssetIds;
  const noneDeletable =
    hasChecked && deletableIds.length === 0 && selectedAssetIds.length > 0;
  const assetTypeLabel = getAssetTypeLabel(selectedAssetIds);
  const translationNamespace = isTrash ? "permanent" : "trash";
  const explanationKey:
    | "body.permanent.explanation"
    | "body.trash.explanation" = isTrash
    ? "body.permanent.explanation"
    : "body.trash.explanation";

  // Skip while closed to avoid querying a selection the user can't see; use
  // idsToDelete so a permission-filtered table doesn't describe stale views.
  // Also skip for permanent delete: the trash explanation's view-count copy
  // only applies to the reversible trash flow, so there's nothing to fetch.
  const tableIds = useMemo(() => {
    if (!isOpen || isTrash) return EMPTY_IDS;
    return idsToDelete
      .map(parseTypedAssetId)
      .filter(isTypedAssetId)
      .filter((parsed) => parsed.assetType === AssetTypes.Table)
      .map((parsed) => parsed.assetId);
  }, [isOpen, isTrash, idsToDelete]);

  const { hasViews, viewCount } = useTablesConnectedAssets(tableIds);

  const undeletableAssets = useMemo(() => {
    if (nonDeletableIds.length === 0) return [];
    return nonDeletableIds
      .map((typedId) => {
        const parsed = parseTypedAssetId(typedId);
        if (!parsed) return;
        // Falls back to the id so the link is never blank when a name wasn't
        // supplied for this asset.
        const name = assetNamesById?.[parsed.assetId] ?? parsed.assetId;
        return {
          id: parsed.assetId,
          title: name,
          type: parsed.assetType as AssetTypes,
        };
      })
      .filter((asset): asset is NonNullable<typeof asset> => !!asset);
  }, [assetNamesById, nonDeletableIds]);

  const handleUndoTrash = useCallback(
    async (assetIds: string[]) => {
      try {
        // A resolved mutation is not a restored asset: per-id outcomes come
        // back in the payload, so report those rather than the ids we asked for.
        const { succeededAssetIds, failedAssetIds } =
          await bulkRestoreAssets.mutateAsync({ assetIds });

        if (failedAssetIds.length === 0) {
          const named = succeededAssetIds.length === 1 && !!singleAssetName;
          createNotification({
            message: t("notifications.trash.restore-success", {
              hasName: named ? "true" : "false",
              name: singleAssetName ?? "",
              count: succeededAssetIds.length,
            }),
            type: "success",
          });
        } else if (succeededAssetIds.length === 0) {
          createNotification({
            message: t("notifications.trash.restore-error"),
            type: "error",
          });
        } else {
          createNotification({
            message: t("notifications.trash.restore-partial", {
              succeededCount: succeededAssetIds.length,
              failedCount: failedAssetIds.length,
            }),
            type: "warning",
          });
        }
      } catch {
        createNotification({
          message: t("notifications.trash.restore-error"),
          type: "error",
        });
      }
    },
    [bulkRestoreAssets, createNotification, singleAssetName, t],
  );

  const handleDelete = useCallback(async () => {
    if (notifyIfOverBulkLimit(itemCount)) {
      setIsOpen(false);
      return;
    }

    setIsDeleting(true);

    try {
      const mutation = isTrash ? bulkDeleteAssets : bulkTrashAssets;
      const { succeededAssetIds, failedAssetIds } = await mutation.mutateAsync({
        assetIds: idsToDelete,
      });

      // One write for every succeeded asset: favorites are replaced wholesale
      // per write, so sequential single-asset calls would race each other.
      if (isTrash) {
        removeManyFromFavorites(
          succeededAssetIds.map(parseTypedAssetId).filter(isTypedAssetId),
        );
      }

      if (failedAssetIds.length === 0) {
        if (isTrash) {
          createNotification({
            message: t("notifications.permanent.success", {
              count: succeededAssetIds.length,
            }),
            type: "success",
          });
        } else {
          const named = succeededAssetIds.length === 1 && !!singleAssetName;
          createNotification({
            title: t("notifications.trash.success", {
              hasName: named ? "true" : "false",
              name: singleAssetName ?? "",
              count: succeededAssetIds.length,
              assetTypeLabel: getAssetTypeLabel(succeededAssetIds),
            }),
            message: (
              <TrashNotification
                onUndo={() => handleUndoTrash(succeededAssetIds)}
              />
            ),
            type: "success",
          });
        }
      } else {
        createNotification({
          title: t("errors.title", { count: failedAssetIds.length }),
          message: t(`notifications.${translationNamespace}.partial` as const, {
            succeededCount: succeededAssetIds.length,
            failedCount: failedAssetIds.length,
          }),
          type: "warning",
        });
      }
    } catch (error) {
      createNotification({
        title: t("errors.title", { count: itemCount }),
        message: getMessageFromNetworkError(
          error as Parameters<typeof getMessageFromNetworkError>[0],
        ),
        type: "error",
      });
    } finally {
      setIsDeleting(false);
      setIsOpen(false);
      onSettled?.();
    }
  }, [
    bulkDeleteAssets,
    bulkTrashAssets,
    createNotification,
    handleUndoTrash,
    idsToDelete,
    isTrash,
    itemCount,
    notifyIfOverBulkLimit,
    onSettled,
    removeManyFromFavorites,
    setIsOpen,
    singleAssetName,
    t,
    translationNamespace,
  ]);

  if (!isOpen) {
    return;
  }

  const hasName = itemCount === 1 && !!singleAssetName;
  const deleteButtonLabel = t(
    `buttons.${translationNamespace}.delete` as const,
    {
      hasName: String(hasName),
      count: itemCount,
      assetTypeLabel,
    },
  );

  const submitDisabledTooltip = noneDeletable
    ? t("buttons.delete-permission-denied-tooltip", {
        count: selectedAssetIds.length,
        assetTypeLabel,
      })
    : undefined;

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      <>
        <ModalDialogHeader textAlign="start">
          {t(`header.${translationNamespace}.title` as const, {
            hasName: String(hasName),
            name: singleAssetName ?? "",
            count: itemCount,
            assetTypeLabel,
          })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          {isPermLoading ? (
            <div className={styles.permissionSpinner}>
              <Spinner />
            </div>
          ) : (
            <>
              <Text color="TextWeaker">
                {t(explanationKey, {
                  count: itemCount,
                  assetTypeLabel,
                  hasViews: String(hasViews),
                  viewCount,
                })}
              </Text>
              {!isTrash && (
                <>
                  <Spacer height={12} />
                  <Text color="TextWeaker">
                    {t("body.trash.connected-assets")}
                  </Text>
                </>
              )}
              {undeletableAssets.length > 0 && (
                <>
                  <Spacer height={12} />
                  <Text color="TextWeaker" fontWeight={600}>
                    {t(
                      `body.${translationNamespace}.undeletable-intro` as const,
                      {
                        count: undeletableAssets.length,
                        assetTypeLabel: getAssetTypeLabel(nonDeletableIds),
                      },
                    )}
                  </Text>
                  <Spacer height={8} />
                  <UndeletableAssetsList assets={undeletableAssets} />
                </>
              )}
            </>
          )}
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button
            variant="tertiary"
            isDisabled={isDeleting}
            onPress={() => setIsOpen(false)}
          >
            {t("buttons.cancel")}
          </Button>

          {submitDisabledTooltip ? (
            <Tooltip
              allowMultiline
              content={submitDisabledTooltip}
              placement="top"
            >
              <TooltipFocusableTrigger>
                <Button variant="danger" isDisabled={true}>
                  {deleteButtonLabel}
                </Button>
              </TooltipFocusableTrigger>
            </Tooltip>
          ) : (
            // TODO: once the legacy hard-delete path is removed, consider a
            // small inline spinner on the views copy instead of just letting
            // it pop in once hasViews/viewCount resolve.
            <Button
              variant="danger"
              isDisabled={isDeleting || isPermLoading}
              isPending={isDeleting}
              onPress={handleDelete}
            >
              {deleteButtonLabel}
            </Button>
          )}
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
}
